/**
 * Kopf jeder Bereichsseite (D-00c): Farbband mit Breadcrumb, Symbol, Name,
 * Beschreibung, Aktionen und Tabs mit Unterstrich in der Bereichsfarbe.
 * Die Farbe des Bands ist aus der Bereichsfarbe abgeleitet (ca. 25 % auf dem
 * Grund); Vorlage fuer feste wie selbst angelegte Bereiche.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { BereichsSymbol } from '@/modules/areas/BereichsSymbol';
import { aktiverTab, bereichsTabs } from '@/domain/areas/tabs';
import { cn } from '@/lib/utils/cn';
import { de } from '@/i18n/de';
import type { Area } from '@/domain/types';

export function BereichsKopf({ area, aktionen }: { area: Area; aktionen?: ReactNode }) {
  const { pathname } = useLocation();
  const tabs = bereichsTabs(area);
  const aktiv = aktiverTab(tabs, pathname);
  const farbe = { '--area': area.color } as CSSProperties;

  return (
    <header data-area style={farbe} className="flex flex-col">
      <div className="h-[150px] rounded-b-none rounded-t-lg px-5 pt-4 sm:px-6"
        style={{ backgroundColor: 'color-mix(in oklch, var(--area-c) 25%, var(--color-canvas))' }}>
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-[12px] text-ink-muted">
          <Link to="/" className="hover:text-ink">Übersicht</Link>
          <ChevronRight className="size-3" aria-hidden="true" />
          <Link to="/bereiche" className="hover:text-ink">{de.areas.title}</Link>
          <ChevronRight className="size-3" aria-hidden="true" />
          <span className="text-ink">{area.name}</span>
        </nav>
      </div>

      <div className="-mt-9 flex flex-col items-start gap-3 px-5 sm:flex-row sm:items-end sm:gap-4 sm:px-6">
        <span className="area-dot grid size-14 shrink-0 sm:size-[72px] place-items-center rounded-[18px] border-4 border-canvas text-canvas">
          <BereichsSymbol name={area.icon} className="size-7 sm:size-8" />
        </span>
        <div className="min-w-0 flex-1 pb-1 sm:min-w-[12rem]">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="min-w-0 text-2xl font-extrabold leading-tight tracking-tight sm:text-[34px]">{area.name}</h1>
            {area.archived && <span className="rounded-full bg-surface-raised px-2 py-0.5 text-[11px] text-ink-muted">{de.areas.archived}</span>}
          </div>
          {area.beschreibung && <p className="mt-1 max-w-prose text-[14px] text-ink-muted">{area.beschreibung}</p>}
        </div>
        {aktionen && <div className="flex flex-wrap items-center gap-2 pb-1">{aktionen}</div>}
      </div>

      {tabs.length > 1 && (
        <nav aria-label="Bereich" className="mt-4 flex gap-1 overflow-x-auto border-b border-line px-5 sm:px-6">
          {tabs.map((t) => (
            <NavLink key={t.key} to={t.to} end
              aria-current={aktiv === t.key ? 'page' : undefined}
              className={cn('-mb-px shrink-0 border-b-2 px-3 py-2.5 text-sm transition-colors',
                aktiv === t.key ? 'font-bold text-ink' : 'border-transparent text-ink-muted hover:text-ink')}
              style={aktiv === t.key ? { borderBottomColor: 'var(--area-c)' } : undefined}>
              {t.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}
